import {useState, useEffect} from 'react'
/** @jsxImportSource @emotion/react */
import {css, keyframes} from '@emotion/react'

import {connect} from 'react-redux'

import {crudAPI} from '../../../apis'

import {useIsMounted} from '../../../hooks'

import {makeGetPerformanceById} from '../../../selectors'

import {
  day, 
  formatDate, 
  formatTime,
  getTopAddress,
  getBottomAddress
} from '../../../utils'

import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'

import { 
  ExpandingText,
  CommentCycler,
  FrostedAspectImage
} from '../../generics'

import RSVPForm from './RSVPForm'
import RSVPButtons from './RSVPButtons'



const bounceIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  60% {
    opacity: 1;
    transform: scale(1.2);
  }
  80% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(1);
  }
`


const styles = {
  root: css`
    width: 500px;
    min-height: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 8px;
    overflow: hidden;
  `,
  content: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 30px;
    color: #fff;
    text-align: center;
    position: relative;
  `,
  title: css`
    color: #fff;
    z-index: 10;
    text-align: center;
    padding-bottom: 15px;
  `,
  fullName: css`
    font-size: 1.2rem;
    font-weight: 400;
    margin: 0;
    margin-bottom: 15px;
  `,
  rsvpCaption: css`
    color: #ff0095;
    text-transform: uppercase;
    font-size: 0.9rem;
    font-weight: 500;
  `,
  location: css`
    font-weight: 600;
    max-width: 60%;
    padding-top: 10px;
  `,
  formContainer: css`
    width: 100%;
    max-width: 300px;
    max-height: 0px;
    opacity: 0;
    transition: max-height 400ms ease-out, opacity 300ms ease-out;
  `,
  formContainerOpen: css`
    max-height: 500px;
    opacity: 1;
  `,
  buttons: css`
    display: flex;
    justify-content: center;
    padding-top: 30px;
    padding-bottom: 15px;
  `,
  completionContainer: css`
    height: 25px;

    p {
      color: #90EE90;
      font-size: 0.9rem;
      font-weight: 600;
      text-transform: uppercase;
    }
  `,
  completionContainerActive: css`
    animation: ${bounceIn} 0.3s ease-out;
  `,
  attendeeContainer: css`
    width: 100%;
    border-radius: 8px;
    padding: 16px 16px 20px 16px;
    box-shadow: inset 0 0 0 1px rgba(240, 240, 240, 0.2);
    background-color: rgba(0, 128, 255, 0.15);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    margin-bottom: 20px;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `,
  caption: css`
    font-size: 0.9rem;
    padding-bottom: 16px;
  `
}


// 1) pastel palette
const pastelColors = [
  '#FFCDD2', '#F8BBD0', '#E1BEE7', '#D1C4E9',
  '#C5CAE9', '#BBDEFB', '#B3E5FC', '#B2EBF2',
  '#B2DFDB', '#C8E6C9', '#DCEDC8', '#F0F4C3',
  '#FFF9C4', '#FFECB3', '#FFE0B2', '#FFDAB9'
]

// 2) simple string→number hash
function hashCode(str) {
  return Array.from(str).reduce((h, c) => h + c.charCodeAt(0), 0)
}

// 3) emojis
const emojiList = ['🎉','🎵','🎤','🎸','🥁','🪕','🎻','🎺','🎹','🎷']
function getEmoji(id) {
  return emojiList[hashCode(id) % emojiList.length]
}

// 4) color picker
function getColor(id) {
  return pastelColors[hashCode(id) % pastelColors.length]
}



const RSVPSheet = ({
  eventID,
  name,
  start,
  close,
  token,
  address,
  userName,
  imageURL, 
  location, 
  isLoaded,
  description,
  isAuthenticated,
  externalImageURL
}) => {
    
  const isMounted = useIsMounted()


  const [complete, setComplete] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [initialSubmissionComplete, setInitialSubmissionComplete] = useState(false)

  const [rsvps, setRsvps] = useState([])
  const [totalConfirmed, setTotalConfirmed] = useState(0)

  const commentItems = rsvps.map(r => ({
    id:      r.id,
    comment: r.attributes.comment?.trim(),
    src:     r.attributes.src,
    name:    r.attributes.name,
    status:  r.attributes.status
  }))
  .filter(item => item.comment && item.status === 'confirmed')

  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    comment: '',
    status: 'pending'
  })

  const [fullName, setFullName] = useState(userName)


  useEffect(() => {
    const url = `/performances/${eventID}/rsvps`

    crudAPI.index(url).then(response => {
      const result         = response?.data?.data
      const totalConfirmed = response?.data?.meta?.total_confirmed
      const parsed         = parseInt(totalConfirmed, 10) || 0

      if (isMounted.current) {
        setRsvps(result)
        setTotalConfirmed(parsed)
      }
    })
    .catch(error => {
      // TODO: Handle error
    })
  }, [eventID, isMounted])



  useEffect(() => {
    if (!eventID || !token) return

    crudAPI
      .show(`/performances/${eventID}/rsvp`, {invitation_token: token})
      .then(resp => {
        const r = resp.data.data
        const contactName = r.attributes.name

        // 1) seed your formData with their existing info:
        setFormData({
          name:    r.attributes.name    || '',
          email:   r.attributes.email   || '',
          phone:   r.attributes.phone   || '',
          comment: r.attributes.comment || '',
          status:  r.attributes.status
        })

        if (contactName) setFullName(contactName)
        setInitialSubmissionComplete(true)
      })
      .catch(err => {
        console.error("failed loading single RSVP:", err)
      })
  }, [eventID, token])




  useEffect(() => {
    if (!complete) return
    const timer = setTimeout(() => {
      if (isMounted.current) {
        setComplete(false)
        setInitialSubmissionComplete(true)
      }
    }, 3000)

    return () => clearTimeout(timer)
  }, [complete, isMounted])




  async function submit(values) {
    const url = `/performances/${eventID}/rsvps`

    setIsSubmitting(true)

    try {
      const response = await crudAPI.create(url, values)
      const newRsvp = response?.data?.data

      setRsvps(prev => {
        const exists = prev.find(r => r.id === newRsvp.id)
        return exists
          ? prev.map(r => r.id === newRsvp.id ? newRsvp : r)
          : [...prev, newRsvp]
      })

      if (isMounted.current) {
        setComplete(true)
        setShowForm(false)
      }
    } catch (error) {
      throw error
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false)
      }
    }
  }



  async function setStatus(newStatus) {
    if (newStatus === formData.status) return
    const updated = {...formData, status: newStatus}

    if (isAuthenticated || updated.email || updated.phone) {
      await submit(updated)
    }

    setFormData(updated)
    if (!initialSubmissionComplete) {
      setShowForm(true)
    }
  }


  return (isLoaded &&
    <div css={styles.root}>
      <FrostedAspectImage src={imageURL || externalImageURL} alt='event poster' aspect='2:3' close={close}>
        <div css={styles.content}>
          <CommentCycler items={commentItems} interval={4000} />

          <div>
            {fullName &&
              <h4 css={styles.fullName}>Hi {fullName}!</h4>
            }
            <p css={styles.rsvpCaption}>
              RSVP FOR EVENT
            </p>
            <h2 css={styles.title}>
              {name}
            </h2>
          </div>

          <p>
            {day(start, 'eee') + ', ' + formatDate(start, 'MMMM do') + ' @ ' + formatTime(start)}
          </p>

          <p css={styles.location}>
            {location}
          </p>

          <div css={styles.address}>
            <p>{getTopAddress(address)}</p>
            <p>{getBottomAddress(address)}</p>
          </div>

          <div css={styles.buttons}>
            <RSVPButtons status={formData.status} setStatus={setStatus} isSubmitting={isSubmitting} />
          </div>


          <div css={[styles.completionContainer, complete && styles.completionContainerActive]}>
            {complete &&
              <p>
                {!initialSubmissionComplete ? "You're all set!" : 'Status updated'}
              </p>
            }
          </div>


          <div css={[styles.formContainer, showForm && styles.formContainerOpen]}>
            {showForm &&
              <RSVPForm formData={formData} isAuthenticated={isAuthenticated} onSubmit={async (values) => {
                const updated = {...formData, ...values}
                await submit(updated)
                setFormData(updated)
              }} />
            }
          </div>


          {totalConfirmed > 2 && 
            <div css={styles.attendeeContainer}>
              <p css={styles.caption}>{`${totalConfirmed.toLocaleString()} Going`}</p>

              <AvatarGroup 
                max={10} 
                spacing={4}
                slotProps={{
                  avatar: {
                    sx: {
                      width: 30,
                      height: 30,
                      fontSize: '23px',
                    }
                  }
                }}
              >
                {rsvps.map(rsvp => {
                  const color = rsvp.src ? undefined : getColor(rsvp.id)

                  return (
                    <Avatar
                      key={rsvp.id}
                      alt={rsvp.name}
                      src={rsvp.src || undefined}
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: '23px', 
                        backgroundColor: color,
                        
                        '&.MuiAvatar-root': {
                          border: `2px solid ${color}`
                        }
                      }}
                    >
                      {/* if no src, render the emoji */}
                      {!rsvp.src && getEmoji(rsvp.id)}
                    </Avatar>
                  )
                })}
              </AvatarGroup>
            </div>
          }

          <div css={styles.description}>
            <ExpandingText text={description} lineCount={5} />
          </div>
        </div>
      </FrostedAspectImage>
    </div>
  )
}




const makeMapStateToProps = () => {
  const getPerformanceById = makeGetPerformanceById()

  const mapStateToProps = (state, props) => {
    const id = props.eventID

    const performance = getPerformanceById(state, id)

    const attributes = performance?.attributes || {}

    const imageURL = attributes.imageDerivatives?.large || attributes.image

    const externalImageURL = attributes.externalImageUrl

    const {username, firstName, lastName} = state.user.attributes 

    return {
      userName: `${firstName || ''} ${lastName || ''}`.trim() || username,
      name: attributes.name || '',
      start: attributes.start || '',
      imageURL: imageURL,
      address: attributes.address,
      location: attributes.location,
      description: attributes.description || '',
      externalImageURL: externalImageURL,
      isLoaded: Boolean(performance.id),
      isAuthenticated: state.user.isAuthenticated
    }
  }

  return mapStateToProps
}



export default connect(makeMapStateToProps, {})(RSVPSheet)



